import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useToast } from '../../../Hooks/ToasContext'
import { ListHCPArtesUseCase } from '../../../Usecase/HCP/Artes/list-hcp-artes-usecase'
import { ListHCPArtesColunaUseCase } from '../../../Usecase/HCP/Artes/list-hcp-artes-coluna-usecase'
import IKanbanBoardHCPArtesSchemaProps from '../../../Components/KanbanBoardHCPArtes/interfaces/kanban-board-hcp-artes-schema.interface'
import {
  HCPArteKanbanColumnKeyEnum,
  IHCPArteKanbanItem,
  IHCPArteKanbanResponse,
} from '../../../Services/HCP/creativeflow/creative-flow.interface'

interface IUseArteAndamentoProps {
  listHCPArtesUseCase: ListHCPArtesUseCase
  listHCPArtesColunaUseCase: ListHCPArtesColunaUseCase
}

export interface IFiltroOpcao {
  label: string
  value: string
}

export type FiltroPrazoType = 'todos' | 'atrasado' | 'no_prazo' | 'sem_prazo'
export type FiltroAprovadoOSType = 'todos' | 'sem_os' | 'com_os'
export type FiltroItensPendentesType = 'todos' | 'completos' | 'com_pendencias'

interface IAprovadoState {
  items: IHCPArteKanbanItem[]
  total: number
  page: number
  hasMore: boolean
}

interface IKanbanBase {
  tasks: IKanbanBoardHCPArtesSchemaProps['tasks']
  baseColumns: IKanbanBoardHCPArtesSchemaProps['columns']
}

const APROVADO_INICIAL: IAprovadoState = { items: [], total: 0, page: 1, hasMore: false }
const PAGE_SIZE = 30

const COLUMN_ORDER: HCPArteKanbanColumnKeyEnum[] = [
  HCPArteKanbanColumnKeyEnum.ARTES_NAO_INICIADAS,
  HCPArteKanbanColumnKeyEnum.ARTE_EM_ANDAMENTO,
  HCPArteKanbanColumnKeyEnum.REVISAO_CLIENTE,
  HCPArteKanbanColumnKeyEnum.ALTERACAO_EM_ANDAMENTO,
  HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
]

const AUTO_REFRESH_MS = 5 * 60 * 1000

function getPendentesParam(filtro: FiltroItensPendentesType): boolean | undefined {
  if (filtro === 'completos') return false
  if (filtro === 'com_pendencias') return true
  return undefined
}

function mapItem(item: IHCPArteKanbanItem, tem_os = false) {
  return {
    pedido_id: item.pedido_id,
    arte_id: item.arte_id,
    numero: item.numero,
    cliente: item.cliente,
    produto_nome: item.produto_nome,
    tiragem: item.tiragem ?? (item.producao?.tiragem ? parseInt(item.producao.tiragem, 10) : 0),
    ordem_de_servico: item.ordem_de_servico,
    designer: item.designer,
    etiquetas: item.fluxo.etiquetas,
    prazo_atual: item.fluxo.prazo_atual,
    atrasado: item.fluxo.atrasado,
    tem_briefing: item.tem_briefing,
    tem_arte: item.tem_arte,
    link_arte_final: item.link_arte_final,
    thumbnail_arte_final: item.thumbnail_arte_final,
    no_prazo_producao: item.no_prazo_producao,
    tem_os,
    itens_pendentes: item.itens_pendentes ?? 0,
    producao: item.producao ? {
      producao_id: item.producao.producao_id,
      etapa: item.producao.etapa,
      data_inicio: item.producao.data_inicio,
      prazo_producao: item.producao.prazo_producao,
      finalizacao: item.producao.finalizacao,
    } : null,
    revisoes_pendentes: item.revisoes_pendentes,
    alteracoes_aprovadas: item.alteracoes_aprovadas,
    alteracoes_entregues: item.alteracoes_entregues,
    perfil_sla: {
      nome: item.fluxo.perfil_sla.nome,
      prazo_producao_horas: item.fluxo.perfil_sla.prazo_producao_horas,
      prazo_alteracao_horas: item.fluxo.perfil_sla.prazo_alteracao_horas,
    },
    criado_em: item.fluxo.criado_em,
    atualizado_em: item.fluxo.atualizado_em,
  }
}

function transformBaseSchema(kanbanResponse: IHCPArteKanbanResponse): IKanbanBase {
  const tasks: IKanbanBoardHCPArtesSchemaProps['tasks'] = {}
  const baseColumns: IKanbanBoardHCPArtesSchemaProps['columns'] = {}

  kanbanResponse.columns.forEach((column) => {
    if (column.key === HCPArteKanbanColumnKeyEnum.APROVADO_FINAL) return

    const taskIds: string[] = []
    column.items.forEach((item) => {
      const taskId = String(item.produto_pedido_id)
      taskIds.push(taskId)
      tasks[taskId] = { id: taskId, content: mapItem(item, false) }
    })

    baseColumns[column.key] = {
      id: column.key,
      title: column.title,
      count: column.count,
      taskIds,
    }
  })

  return { tasks, baseColumns }
}

export function useArteAndamento({
  listHCPArtesUseCase,
  listHCPArtesColunaUseCase,
}: IUseArteAndamentoProps) {
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [kanbanBase, setKanbanBase] = useState<IKanbanBase | undefined>()
  const [semOS, setSemOS] = useState<IAprovadoState>(APROVADO_INICIAL)
  const [comOS, setComOS] = useState<IAprovadoState>(APROVADO_INICIAL)
  const [lastUpdated, setLastUpdated] = useState<Date | undefined>()
  const { showToast } = useToast()

  // ── Filtros persistidos na URL ────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams()
  const filtroDesigner        = searchParams.get('designer') ?? ''
  const filtroPrazo           = (searchParams.get('prazo')     as FiltroPrazoType)           ?? 'todos'
  const filtroEtiqueta        = searchParams.get('etiqueta')  ?? ''
  const filtroProduto         = searchParams.get('produto')   ?? ''
  const filtroBusca           = searchParams.get('busca')     ?? ''
  const filtroAprovadoOS      = (searchParams.get('os')        as FiltroAprovadoOSType)       ?? 'todos'
  const filtroItensPendentes  = (searchParams.get('pendentes') as FiltroItensPendentesType)  ?? 'todos'

  const setParam = (key: string, value: string, defaultVal: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value && value !== defaultVal) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: true }
    )

  const setFiltroDesigner       = (v: string)                    => setParam('designer', v, '')
  const setFiltroPrazo          = (v: FiltroPrazoType)           => setParam('prazo',    v, 'todos')
  const setFiltroEtiqueta       = (v: string)                    => setParam('etiqueta', v, '')
  const setFiltroProduto        = (v: string)                    => setParam('produto',  v, '')
  const setFiltroBusca          = (v: string)                    => setParam('busca',    v, '')
  const setFiltroAprovadoOS     = (v: FiltroAprovadoOSType)      => setParam('os',       v, 'todos')
  const setFiltroItensPendentes = (v: FiltroItensPendentesType)  => setParam('pendentes',v, 'todos')

  const initialFetchDoneRef = useRef(false)
  // Protege contra race condition: cada fetch recebe um ID único;
  // ao resolver, descarta o resultado se um fetch mais recente já foi iniciado.
  const requestIdRef = useRef(0)
  // Mantém sempre a versão mais recente de fetchArtes para o setInterval.
  const latestFetchRef = useRef<() => Promise<void>>(() => Promise.resolve())

  // Busca semOS e comOS para a página 1 com o filtro de pendentes atual
  const fetchAprovadosPage1 = async (pendentesFilter: FiltroItensPendentesType) => {
    const myId = ++requestIdRef.current
    const itensPendentesParam = getPendentesParam(pendentesFilter)
    const [semOSResult, comOSResult] = await Promise.all([
      listHCPArtesColunaUseCase.handle({
        status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
        tem_os: false,
        page: 1,
        page_size: PAGE_SIZE,
        itens_pendentes: itensPendentesParam,
      }),
      listHCPArtesColunaUseCase.handle({
        status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
        tem_os: true,
        page: 1,
        page_size: PAGE_SIZE,
        itens_pendentes: itensPendentesParam,
      }),
    ])
    if (requestIdRef.current !== myId) return // resultado obsoleto — fetch mais recente iniciado
    if (!semOSResult.isFailure && semOSResult.getValue()) {
      const d = semOSResult.getValue()!
      setSemOS({ items: d.results, total: d.count, page: 1, hasMore: d.next !== null })
    }
    if (!comOSResult.isFailure && comOSResult.getValue()) {
      const d = comOSResult.getValue()!
      setComOS({ items: d.results, total: d.count, page: 1, hasMore: d.next !== null })
    }
  }

  const fetchArtes = async () => {
    const myId = ++requestIdRef.current
    setLoading(true)

    const [kanbanResult, semOSResult, comOSResult] = await Promise.all([
      listHCPArtesUseCase.handle({}),
      listHCPArtesColunaUseCase.handle({
        status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
        tem_os: false,
        page: 1,
        page_size: PAGE_SIZE,
        itens_pendentes: getPendentesParam(filtroItensPendentes),
      }),
      listHCPArtesColunaUseCase.handle({
        status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
        tem_os: true,
        page: 1,
        page_size: PAGE_SIZE,
        itens_pendentes: getPendentesParam(filtroItensPendentes),
      }),
    ])

    if (requestIdRef.current !== myId) return // resultado obsoleto

    if (kanbanResult.isFailure) {
      showToast(String(kanbanResult?.error?.error ?? 'Erro ao carregar artes'), 'error')
      setLoading(false)
      return
    }
    if (semOSResult.isFailure) {
      showToast(String(semOSResult?.error?.error ?? 'Erro ao carregar aprovados'), 'error')
      setLoading(false)
      return
    }
    if (comOSResult.isFailure) {
      showToast(String(comOSResult?.error?.error ?? 'Erro ao carregar aprovados com OS'), 'error')
      setLoading(false)
      return
    }

    const kanbanData = kanbanResult.getValue()
    const semOSData = semOSResult.getValue()
    const comOSData = comOSResult.getValue()
    if (!kanbanData || !semOSData || !comOSData) { setLoading(false); return }

    setKanbanBase(transformBaseSchema(kanbanData))
    setSemOS({ items: semOSData.results, total: semOSData.count, page: 1, hasMore: semOSData.next !== null })
    setComOS({ items: comOSData.results, total: comOSData.count, page: 1, hasMore: comOSData.next !== null })
    setLastUpdated(new Date())
    setLoading(false)
    initialFetchDoneRef.current = true
  }

  const loadMoreAprovados = async () => {
    if (loadingMore) return
    setLoadingMore(true)

    const promessas: Promise<void>[] = []

    if ((filtroAprovadoOS === 'todos' || filtroAprovadoOS === 'sem_os') && semOS.hasMore) {
      promessas.push(
        listHCPArtesColunaUseCase
          .handle({
            status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
            tem_os: false,
            page: semOS.page + 1,
            page_size: PAGE_SIZE,
            itens_pendentes: getPendentesParam(filtroItensPendentes),
          })
          .then((r) => {
            if (!r.isFailure) {
              const d = r.getValue()!
              setSemOS((prev) => ({
                ...prev,
                items: [...prev.items, ...d.results],
                page: prev.page + 1,
                hasMore: d.next !== null,
              }))
            }
          })
      )
    }

    if ((filtroAprovadoOS === 'todos' || filtroAprovadoOS === 'com_os') && comOS.hasMore) {
      promessas.push(
        listHCPArtesColunaUseCase
          .handle({
            status: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
            tem_os: true,
            page: comOS.page + 1,
            page_size: PAGE_SIZE,
            itens_pendentes: getPendentesParam(filtroItensPendentes),
          })
          .then((r) => {
            if (!r.isFailure) {
              const d = r.getValue()!
              setComOS((prev) => ({
                ...prev,
                items: [...prev.items, ...d.results],
                page: prev.page + 1,
                hasMore: d.next !== null,
              }))
            }
          })
      )
    }

    await Promise.all(promessas)
    setLoadingMore(false)
  }

  // Mantém latestFetchRef sempre apontando para a versão mais recente de fetchArtes
  // (evita stale closure no setInterval — o filtro ativo sempre é respeitado)
  useLayoutEffect(() => {
    latestFetchRef.current = fetchArtes
  })

  useEffect(() => {
    const interval = setInterval(() => { latestFetchRef.current() }, AUTO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [])

  // Quando o filtro de pendentes muda, re-busca os aprovados na API com o novo parâmetro
  useEffect(() => {
    if (!initialFetchDoneRef.current) return
    setSemOS(APROVADO_INICIAL)
    setComOS(APROVADO_INICIAL)
    setLoadingMore(true)
    fetchAprovadosPage1(filtroItensPendentes).finally(() => setLoadingMore(false))
  }, [filtroItensPendentes])

  // Entries para o filtro atual (sem remapear ainda — produto_pedido_id necessário)
  const aprovadoEntries = useMemo(
    (): Array<{ item: IHCPArteKanbanItem; temOS: boolean }> => {
      switch (filtroAprovadoOS) {
        case 'sem_os': return semOS.items.map((item) => ({ item, temOS: false }))
        case 'com_os': return comOS.items.map((item) => ({ item, temOS: true }))
        default: return [
          ...semOS.items.map((item) => ({ item, temOS: false })),
          ...comOS.items.map((item) => ({ item, temOS: true })),
        ]
      }
    },
    [semOS.items, comOS.items, filtroAprovadoOS]
  )

  const aprovadoTotal = useMemo(() => {
    switch (filtroAprovadoOS) {
      case 'sem_os': return semOS.total
      case 'com_os': return comOS.total
      default: return semOS.total + comOS.total
    }
  }, [semOS.total, comOS.total, filtroAprovadoOS])

  const aprovadoHasMore = useMemo(() => {
    switch (filtroAprovadoOS) {
      case 'sem_os': return semOS.hasMore
      case 'com_os': return comOS.hasMore
      default: return semOS.hasMore || comOS.hasMore
    }
  }, [semOS.hasMore, comOS.hasMore, filtroAprovadoOS])

  // Itens efetivamente carregados (antes do filtro de pendentes)
  const aprovadoCarregados = useMemo(() => {
    switch (filtroAprovadoOS) {
      case 'sem_os': return semOS.items.length
      case 'com_os': return comOS.items.length
      default: return semOS.items.length + comOS.items.length
    }
  }, [semOS.items.length, comOS.items.length, filtroAprovadoOS])

  // Schema completo: base + aprovados do filtro atual
  const kanbanSchemaBase = useMemo((): IKanbanBoardHCPArtesSchemaProps | undefined => {
    if (!kanbanBase) return undefined

    const tasks = { ...kanbanBase.tasks }
    const aprovadoTaskIds: string[] = []

    aprovadoEntries.forEach(({ item, temOS }) => {
      const taskId = String(item.produto_pedido_id)
      aprovadoTaskIds.push(taskId)
      tasks[taskId] = { id: taskId, content: mapItem(item, temOS) }
    })

    const columns: IKanbanBoardHCPArtesSchemaProps['columns'] = {
      ...kanbanBase.baseColumns,
      [HCPArteKanbanColumnKeyEnum.APROVADO_FINAL]: {
        id: HCPArteKanbanColumnKeyEnum.APROVADO_FINAL,
        title: 'Aprovado (final)',
        count: aprovadoTotal,
        taskIds: aprovadoTaskIds,
      },
    }

    return { tasks, columns, columnOrder: COLUMN_ORDER }
  }, [kanbanBase, aprovadoEntries, aprovadoTotal])

  const designersDisponiveis = useMemo((): IFiltroOpcao[] => {
    if (!kanbanSchemaBase) return []
    const map = new Map<string, string>()
    Object.values(kanbanSchemaBase.tasks).forEach((task) => {
      if (task.content.designer) {
        map.set(String(task.content.designer.id), task.content.designer.nome)
      }
    })
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [kanbanSchemaBase])

  const etiquetasDisponiveis = useMemo((): IFiltroOpcao[] => {
    if (!kanbanSchemaBase) return []
    const map = new Map<string, { label: string; cor: string }>()
    Object.values(kanbanSchemaBase.tasks).forEach((task) => {
      task.content.etiquetas.forEach((e) => {
        map.set(String(e.id), { label: e.nome, cor: e.cor })
      })
    })
    return Array.from(map.entries())
      .map(([value, { label }]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [kanbanSchemaBase])

  const produtosDisponiveis = useMemo((): IFiltroOpcao[] => {
    if (!kanbanSchemaBase) return []
    const uniqueProducts = new Set<string>()
    Object.values(kanbanSchemaBase.tasks).forEach((task) => {
      if (task.content.produto_nome) {
        uniqueProducts.add(task.content.produto_nome)
      }
    })
    return Array.from(uniqueProducts)
      .sort()
      .map((p) => ({ value: p, label: p }))
  }, [kanbanSchemaBase])

  // Filtros client-side (designer, prazo, etiqueta, produto, busca)
  const kanbanSchemaFiltrado = useMemo((): IKanbanBoardHCPArtesSchemaProps | undefined => {
    if (!kanbanSchemaBase) return undefined

    const temFiltroAtivo = filtroDesigner || filtroPrazo !== 'todos' || filtroEtiqueta || filtroProduto || filtroBusca.trim()
    if (!temFiltroAtivo) return kanbanSchemaBase

    const buscaNorm = filtroBusca.trim().toLowerCase()
    const tasksFiltradas = new Set<string>()

    Object.values(kanbanSchemaBase.tasks).forEach((task) => {
      const c = task.content

      if (filtroDesigner && String(c.designer?.id ?? '') !== filtroDesigner) return
      if (filtroPrazo === 'atrasado' && !c.atrasado) return
      if (filtroPrazo === 'no_prazo' && (c.prazo_atual === null || c.atrasado)) return
      if (filtroPrazo === 'sem_prazo' && c.prazo_atual !== null) return
      if (filtroEtiqueta && !c.etiquetas.some((e) => String(e.id) === filtroEtiqueta)) return
      if (filtroProduto && c.produto_nome !== filtroProduto) return

      if (buscaNorm) {
        const match =
          c.numero.toLowerCase().includes(buscaNorm) ||
          c.cliente.toLowerCase().includes(buscaNorm) ||
          c.produto_nome.toLowerCase().includes(buscaNorm)
        if (!match) return
      }

      tasksFiltradas.add(task.id)
    })

    const novasColumns = { ...kanbanSchemaBase.columns }
    COLUMN_ORDER.forEach((colId) => {
      const col = kanbanSchemaBase.columns[colId]
      if (!col) return
      const novasIds = col.taskIds.filter((id) => tasksFiltradas.has(id))
      // APROVADO_FINAL usa o count da API (já filtrado por pendentes no servidor)
      const isAprovado = colId === HCPArteKanbanColumnKeyEnum.APROVADO_FINAL
      const count = isAprovado ? col.count : novasIds.length
      novasColumns[colId] = { ...col, taskIds: novasIds, count }
    })

    return { ...kanbanSchemaBase, columns: novasColumns }
  }, [kanbanSchemaBase, filtroDesigner, filtroPrazo, filtroEtiqueta, filtroProduto, filtroBusca])

  const limparFiltros = () => setSearchParams(new URLSearchParams(), { replace: true })

  const temFiltroAtivo = !!(filtroDesigner || filtroPrazo !== 'todos' || filtroEtiqueta || filtroProduto || filtroBusca.trim() || filtroAprovadoOS !== 'todos' || filtroItensPendentes !== 'todos')

  return {
    fetchArtes,
    kanbanSchema: kanbanSchemaFiltrado,
    loading,
    loadingMore,
    lastUpdated,
    filtroDesigner, setFiltroDesigner,
    filtroPrazo, setFiltroPrazo,
    filtroEtiqueta, setFiltroEtiqueta,
    filtroProduto, setFiltroProduto,
    filtroBusca, setFiltroBusca,
    filtroAprovadoOS, setFiltroAprovadoOS,
    filtroItensPendentes, setFiltroItensPendentes,
    aprovadoHasMore,
    aprovadoTotal,
    aprovadoCarregados,
    semOSCarregados: semOS.items.length,
    comOSCarregados: comOS.items.length,
    semOSTotal: semOS.total,
    comOSTotal: comOS.total,
    loadMoreAprovados,
    limparFiltros,
    temFiltroAtivo,
    designersDisponiveis,
    etiquetasDisponiveis,
    produtosDisponiveis,
  }
}
